import { useCallback, useMemo } from 'react';
import { useRecoilValue } from 'recoil';

import backend from '../../../lib/backend';
import financingForm from '../../../state/forms/eslForms/financingForm';
import { CommonApplicationProfile } from '../../../types/commonApplicationProfile';
import { ValidationError } from '../../../types/errors/validation';
import { RequireAdditionalFinancingOption } from '../../../types/eslProfileFinancingForm';

/**
 * Interface for the return type of the useFinancingForm hook
 */
interface UseFinancingForm { submitForm: () => Promise<CommonApplicationProfile> }

/**
 * Custom hook to manage the state and submission logic for the financing form.
 *
 * @returns An object containing the submitForm handler function
 */
const useFinancingForm = (): UseFinancingForm => {
  // Form Values
  const existingStudentLoans  = useRecoilValue(financingForm.fields.existingStudentLoans.valueState);
  const additionalFinancing  = useRecoilValue(financingForm.fields.additionalFinancing.valueState);

  // Form Valid
  const existingStudentLoansValid = useRecoilValue(financingForm.fields.existingStudentLoans.validState);
  const additionalFinancingValid = useRecoilValue(financingForm.fields.additionalFinancing.validState);

  const validateForm = useCallback(() => {
    return [ existingStudentLoansValid, additionalFinancingValid ].every((value) => value);
  }, [ existingStudentLoansValid, additionalFinancingValid ]);

  const validForm = useMemo(validateForm, [validateForm]);

  const submitForm = useCallback(async () => {
    if (!validForm) throw new ValidationError();

    const { data: commonApplicationProfile } = await backend.updateCommonApplicationProfileFinancingInfo({
      // Types passed explicitly since non-null validation was done on validateForm
      existing_student_loans: existingStudentLoans as boolean,
      additional_financing: additionalFinancing as RequireAdditionalFinancingOption,
    });

    return commonApplicationProfile;
  }, [ validForm,  existingStudentLoans, additionalFinancing ]);

  return { submitForm };
};

export default useFinancingForm;
